import { ButtonBase, Rating, Typography, styled } from '@mui/material'
import clsx from 'clsx'
import { useCallback, useMemo, useState } from 'react'
import { StarIcon, StarOutlinedIcon } from '@weblite/icons'
import { CallType } from '@weblite/ts-action/media-proxy'
import { useHistoryCustomBack } from 'weblite-router'
import { BottomDrawer } from '$common/bottom-drawer'
import { t } from '$i18n'
import { nothing } from '$helper/functions/utils'
import { LTR } from '$common/ltr'
import { MultilineInput } from '$common/input'
import { PrimaryButton } from '$common/buttons'
import { usePrevious } from '$common/react-hooks/use-previous'

const qualityDescriptors: { [key: number]: string } = {
  1: t('call.callFeedbackDialog.qualityDescriptors.veryBad'),
  2: t('call.callFeedbackDialog.qualityDescriptors.bad'),
  3: t('call.callFeedbackDialog.qualityDescriptors.normal'),
  4: t('call.callFeedbackDialog.qualityDescriptors.good'),
  5: t('call.callFeedbackDialog.qualityDescriptors.veryGood'),
}

const baseProblemOptions = [
  t('call.callFeedbackDialog.problems.noImage'),
  t('call.callFeedbackDialog.problems.disconnectingAndConnectingTheImage'),
  t('call.callFeedbackDialog.problems.noAudio'),
  t('call.callFeedbackDialog.problems.disconnectingAndConnectingTheAudio'),
]

export interface CallFeedbackType {
  rating: number
  problem?: string
  problemDescription?: string
}

export interface Props {
  isOpen?: boolean
  onConfirm: (params: CallFeedbackType) => unknown
  callType: CallType
}

export function CallFeedback({ isOpen = false, onConfirm, callType }: Props) {
  useHistoryCustomBack(nothing, [])

  const problemOptions = useMemo(() => {
    return callType === 'tribune'
      ? baseProblemOptions.slice(2)
      : baseProblemOptions
  }, [callType])

  const [rating, setRating] = useState<number | null>(null)
  const [selectedProblems, setSelectedProblems] = useState<string[]>([])
  const [problemDescription, setProblemDescription] = useState<string>()

  const qualityDescription = useMemo(() => {
    if (rating && rating in qualityDescriptors) {
      const color =
        rating < 3 ? 'error500' : rating === 3 ? 'secondary700' : 'success700'
      return { text: qualityDescriptors[rating], color }
    }
    return undefined
  }, [rating])

  const previousQualityDescription = usePrevious(qualityDescription)

  const showProblemSelector = rating && rating !== 5

  const submitButtonDisabled = Boolean(
    !rating ||
      (showProblemSelector && !selectedProblems.length && !problemDescription),
  )

  const handleSelectProblem = useCallback((problem: string) => {
    setSelectedProblems(state => {
      const stateCopy = [...state]
      const index = stateCopy.findIndex(p => p === problem)
      if (index === -1) {
        stateCopy.push(problem)
      } else {
        stateCopy.splice(index, 1)
      }
      return stateCopy
    })
  }, [])

  return (
    <BottomDrawer isOpen={isOpen} onClose={nothing}>
      <Root>
        <Typography
          textAlign="center"
          variant="subtitle2"
          color="neutral900"
          mb="16px"
        >
          {t('call.callFeedbackDialog.title')(t(`call.${callType}`), callType)}
        </Typography>
        <RatingContainer>
          <LTR sx={{ height: '40px', mb: '4px' }}>
            <Rating
              name="live-feedback"
              value={rating}
              onChange={(_e, newValue) => setRating(newValue)}
              size="large"
              icon={
                <StarIcon
                  sx={{
                    color: 'secondary500',
                    width: '28px',
                    height: '28px',
                    margin: '6px 8px',
                  }}
                />
              }
              emptyIcon={
                <StarOutlinedIcon
                  sx={{
                    width: '28px',
                    height: '28px',
                    margin: '6px 8px',
                    color: 'neutral400',
                  }}
                />
              }
            />
          </LTR>
          <Typography
            textAlign="center"
            variant="subtitle3"
            color={qualityDescription?.color || 'neutral000'}
            sx={{
              transition: 'color 0.15s ease',
            }}
          >
            «
            {qualityDescription?.text || previousQualityDescription?.text || ''}
            »
          </Typography>
        </RatingContainer>
        {showProblemSelector && (
          <>
            <Typography
              width="100%"
              mb="8px"
              textAlign="right"
              variant="subtitle3"
              color="neutral600"
            >
              {t('call.callFeedbackDialog.problems.title')}
            </Typography>
            <ProblemsContainer>
              {problemOptions.map(p => (
                <ProblemButton
                  key={p}
                  onClick={() => handleSelectProblem(p)}
                  className={clsx({ selected: selectedProblems.includes(p) })}
                >
                  {p}
                </ProblemButton>
              ))}
            </ProblemsContainer>
            <MultilineInput
              sx={{ width: '100%' }}
              placeholder={t('call.callFeedbackDialog.inputPlaceholder')}
              value={problemDescription || ''}
              onChange={setProblemDescription}
            />
          </>
        )}

        <PrimaryButton
          text={t('call.callFeedbackDialog.confirm')}
          onClick={() => {
            if (rating)
              onConfirm({
                rating,
                problem: showProblemSelector
                  ? selectedProblems.join(' - ')
                  : undefined,
                problemDescription: showProblemSelector
                  ? problemDescription
                  : undefined,
              })
          }}
          sx={{ marginTop: '32px' }}
          fullWidth
          disabled={submitButtonDisabled}
        />
      </Root>
    </BottomDrawer>
  )
}

const Root = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  padding: 24px 16px;
`

const RatingContainer = styled('div')`
  width: 100%;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ProblemsContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  row-gap: 8px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  width: 100%;
`

const ProblemButton = styled(ButtonBase)`
  height: 40px;
  border-radius: 20px;
  width: calc(50% - 4px);
  border: 1px solid ${({ theme }) => theme.palette.neutral300};
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.palette.neutral000};
  color: ${({ theme }) => theme.palette.neutral700};
  &:hover,
  &:active {
    background-color: ${({ theme }) => theme.palette.neutral000};
  }
  &.selected {
    background-color: ${({ theme }) => theme.palette.primary50};
    border-color: ${({ theme }) => theme.palette.primary300};
    color: ${({ theme }) => theme.palette.primary500};
    &:hover,
    &:active {
      background-color: ${({ theme }) => theme.palette.primary50};
    }
  }
`
